import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, BarChart3 } from "lucide-react";
import api from "@/lib/api";

/**
 * LeaderboardClinicalCard — 2026-02-16 (Laura)
 *
 * Graduatoria completa per piacevolezza media, per ognuna delle 5 dimensioni
 * (luoghi, attività, contesti sociali, momenti, giorni). Visibile SOLO nella
 * Modalità Clinico, dentro "Approfondimenti clinici".
 *
 * Mostra: nome categoria, N osservazioni, frequenza %, mood medio, mood SD,
 * delta dalla media generale dell'utente. Barretta visiva per il mood medio.
 */

const DIM_TITLES = {
  location: "Luoghi",
  activity: "Attività",
  social_context: "Contesti sociali",
  time_of_day: "Momento della giornata",
  weekday: "Giorno della settimana",
};

const moodColor = (mood, overall) => {
  if (mood == null) return "#94a3b8";
  if (mood >= overall + 1) return "#10b981"; // emerald
  if (mood <= overall - 1) return "#ef4444"; // red
  return "#a8a29e"; // stone
};

const formatIt = (x, digits = 1) =>
  x == null ? "—" : Number(x).toFixed(digits).replace(".", ",");

const DimensionTable = ({ title, items, overall }) => {
  if (!items || items.length === 0) {
    return (
      <Card className="border-dashed" data-testid={`leaderboard-${title.toLowerCase().replace(/\s+/g, "-")}-empty`}>
        <CardContent className="p-2.5">
          <p className="text-[11px] font-semibold text-stone-700 mb-1">{title}</p>
          <p className="text-[10px] text-stone-500 italic">
            Nessuna categoria con N ≥ 5.
          </p>
        </CardContent>
      </Card>
    );
  }
  return (
    <Card className="border" data-testid={`leaderboard-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <CardContent className="p-2.5">
        <p className="text-[11px] font-semibold text-stone-800 mb-1.5">{title}</p>
        <div className="space-y-1">
          {items.map((it) => {
            const bar = Math.max(0, Math.min(100, (it.mood_avg / 10) * 100));
            const c = moodColor(it.mood_avg, overall);
            return (
              <div key={it.key} className="space-y-0.5" data-testid={`leaderboard-row-${it.key}`}>
                <div className="flex items-baseline justify-between gap-2 text-[10.5px]">
                  <span className="text-stone-800 truncate font-medium">{it.label}</span>
                  <span className="tabular-nums text-stone-600 shrink-0">
                    {formatIt(it.mood_avg)}/10
                    <span className="text-stone-400"> · SD {formatIt(it.mood_sd)} · n={it.n}</span>
                  </span>
                </div>
                <div className="relative h-1.5 rounded-full bg-stone-100 overflow-hidden">
                  <div
                    className="absolute left-0 top-0 h-full rounded-full transition-all"
                    style={{ width: `${bar}%`, backgroundColor: c }}
                  />
                  {/* tacca della media generale */}
                  <div
                    className="absolute top-0 h-full w-px bg-stone-400"
                    style={{ left: `${Math.max(0, Math.min(100, (overall / 10) * 100))}%` }}
                    title={`Tua media generale ${formatIt(overall)}/10`}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

const LeaderboardClinicalCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.get("/voice-mood/leaderboard?days=365")
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  if (loading) {
    return (
      <Card className="border" data-testid="leaderboard-loading">
        <CardContent className="p-4 flex items-center justify-center">
          <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (!data || !data.dimensions) {
    return (
      <Card className="border border-dashed" data-testid="leaderboard-empty">
        <CardContent className="p-3">
          <p className="text-[11px] text-stone-500 italic">
            Graduatoria non ancora disponibile (servono almeno alcune categorie con N ≥ 5).
          </p>
        </CardContent>
      </Card>
    );
  }

  const overall = data.overall_mood_avg ?? 5;

  return (
    <Card className="border" data-testid="leaderboard-card">
      <CardHeader className="pb-1.5 px-3 pt-3">
        <CardTitle className="text-xs sm:text-sm flex items-center gap-1.5">
          <BarChart3 className="w-4 h-4" /> Graduatoria completa per piacevolezza
        </CardTitle>
        <p className="text-[10px] text-stone-500 leading-tight">
          Per ogni dimensione, categorie con N ≥ {data.min_obs} ordinate per umore medio.
          Tua media generale: <span className="tabular-nums font-medium">{formatIt(overall)}/10</span>.
          Tacca verticale = la tua media. Colore: verde sopra +1, rosso sotto −1, grigio in mezzo.
        </p>
      </CardHeader>
      <CardContent className="px-3 pb-3 space-y-2">
        <DimensionTable title={DIM_TITLES.location} items={data.dimensions.location} overall={overall} />
        <DimensionTable title={DIM_TITLES.activity} items={data.dimensions.activity} overall={overall} />
        <DimensionTable title={DIM_TITLES.social_context} items={data.dimensions.social_context} overall={overall} />
        <DimensionTable title={DIM_TITLES.time_of_day} items={data.dimensions.time_of_day} overall={overall} />
        <DimensionTable title={DIM_TITLES.weekday} items={data.dimensions.weekday} overall={overall} />
      </CardContent>
    </Card>
  );
};

export default LeaderboardClinicalCard;
